import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import styled from 'styled-components';
import { Check, Monitor, Moon, Sun } from 'lucide-react';

import {
  resolvedThemeVar,
  setThemeMode,
  themeModeVar,
  ThemeMode,
} from 'core/state/themeState';
import { T } from 'services/ThemeTokens';
import { LAYERS } from 'components/constants';

const MODES: { mode: ThemeMode; Icon: typeof Sun }[] = [
  { mode: 'system', Icon: Monitor },
  { mode: 'light', Icon: Sun },
  { mode: 'dark', Icon: Moon },
];

/**
 * ТЗ 0119 — тема в шапке: квадрат того же вида, что «⌘» и колокольчик, со
 * значком видимой темы; по нажатию — короткое меню «Как в системе /
 * Светлая / Тёмная». Одной кнопкой, а не сегментом: на 1440 сегмент из
 * трёх значков налезал на пункты меню слева.
 */
export const ThemeSwitcher = () => {
  const { t } = useTranslation();
  const mode = useReactiveVar(themeModeVar);
  const resolved = useReactiveVar(resolvedThemeVar);
  const [open, setOpen] = React.useState(false);
  // пункт под фокусом клавиатуры (roving tabindex); при открытии — выбранный
  const [focusIndex, setFocusIndex] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = 'theme-switcher-menu';

  const openMenu = () => {
    setFocusIndex(
      Math.max(
        0,
        MODES.findIndex((item) => item.mode === mode),
      ),
    );
    setOpen(true);
  };

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  // фокус за пунктом, пока меню открыто
  React.useEffect(() => {
    if (open) itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  // щелчок мимо меню закрывает его, фокус не трогаем (он ушёл туда, куда щёлкнули)
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const last = MODES.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: focusIndex === last ? 0 : focusIndex + 1,
      ArrowUp: focusIndex === 0 ? last : focusIndex - 1,
      Home: 0,
      End: last,
    };
    if (event.key in moves) {
      event.preventDefault();
      setFocusIndex(moves[event.key]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      // Tab уводит фокус дальше по странице — меню закрывается за ним
      setOpen(false);
    }
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      openMenu();
    }
  };

  const visibleIcon = resolved === 'dark' ? Moon : Sun;
  const TriggerIcon = mode === 'system' ? Monitor : visibleIcon;
  const label = `${t('theme.label')}: ${t(`theme.${mode}`)}`;

  return (
    <Root ref={rootRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <TriggerIcon size={18} strokeWidth={2} aria-hidden="true" />
      </Trigger>
      {open ? (
        <Menu
          id={menuId}
          role="menu"
          aria-label={t('theme.label')}
          onKeyDown={onMenuKeyDown}
        >
          {MODES.map(({ mode: item, Icon }, index) => (
            <Item
              key={item}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={mode === item}
              tabIndex={index === focusIndex ? 0 : -1}
              $active={mode === item}
              onClick={() => {
                setThemeMode(item);
                close(true);
              }}
            >
              <Icon size={15} strokeWidth={2} aria-hidden="true" />
              <ItemLabel>{t(`theme.${item}`)}</ItemLabel>
              {mode === item ? (
                <CheckMark>
                  <Check size={14} strokeWidth={2.25} aria-hidden="true" />
                </CheckMark>
              ) : null}
            </Item>
          ))}
        </Menu>
      ) : null}
    </Root>
  );
};

const Root = styled.div`
  position: relative;
`;

/* Цвета — как у соседних квадратов шапки («⌘», колокольчик), чтобы ряд был
   одинаковым; они на тех же переменных палитры. */
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--k-border-dfe6f1);
  border-radius: 8px;
  cursor: pointer;
  color: var(--k-fg-616e78);
  background: var(--k-bg-ffffff);
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover,
  &[aria-expanded='true'] {
    color: ${T.accentText};
    border-color: ${T.accentText};
  }

  &:focus-visible {
    outline: 2px solid ${T.accentText};
    outline-offset: 2px;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  /* внутри шапки (свой слой LAYERS.HEADER) — потолок содержимого достаточен */
  z-index: ${LAYERS.CONTENT_MAX};
  min-width: 176px;
  padding: 4px;
  border: 1px solid ${T.border};
  border-radius: 10px;
  background: ${T.surfaceRaised};
  box-shadow: ${T.shadow};
`;

const Item = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: ${({ $active }) => ($active ? T.text : T.text2)};
  background: ${({ $active }) => ($active ? T.selected : 'transparent')};

  &:hover {
    color: ${T.text};
    background: ${T.hover};
  }

  &:focus-visible {
    outline: 2px solid ${T.accentText};
    outline-offset: -2px;
  }
`;

const ItemLabel = styled.span`
  flex: 1;
`;

const CheckMark = styled.span`
  display: inline-flex;
  color: ${T.accentText};
`;
